import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GraduationCap, Clock, BadgeCheck, Ban, FileText, ExternalLink, Loader2 } from "lucide-react";
import { getPatashalaEnrollments, patchPatashalaEnrollment, getPatashalaFileUrl, apiErr } from "../lib/api";
import { PageHead, Stat, Modal, Empty, BtnGhost } from "./_ui";

const STATUS = {
  new: { t: "New", c: "bg-[#FBF3E7] text-[#B98A2E]" },
  verified: { t: "Verified", c: "bg-[#E6F0EE] text-[#2F7A6B]" },
  rejected: { t: "Rejected", c: "bg-[#FDECEA] text-[#C62828]" },
};
const Badge = ({ s }) => {
  const x = STATUS[s] || STATUS.new;
  return <span className={`rounded-full px-2 py-0.5 font-body text-[11px] font-semibold ${x.c}`}>{x.t}</span>;
};
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return "—"; } };

export default function PatashalaStudents() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ total: 0, new: 0, verified: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [opening, setOpening] = useState(null);

  const load = () => {
    setLoading(true);
    getPatashalaEnrollments()
      .then((d) => { setRows(d.enrollments); setCounts(d.counts); })
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const viewDoc = async (fileId) => {
    if (!fileId) return toast.error("No document uploaded");
    setOpening(fileId);
    try {
      const url = await getPatashalaFileUrl(fileId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(apiErr(e, "Could not open document")); }
    finally { setOpening(null); }
  };

  const setStatus = async (id, status) => {
    try {
      await patchPatashalaEnrollment(id, status);
      toast.success(status === "verified" ? "Marked verified" : status === "rejected" ? "Marked rejected" : "Reset to new");
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      setDetail((d) => (d && d.id === id ? { ...d, status } : d));
      setCounts((c) => {
        const next = { ...c }; return next; // recomputed on reload
      });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-7 md:px-8" data-testid="admin-patashala">
      <PageHead eyebrow="Talent pipeline" title="Patashala Students"
        desc="Final-year graduates who enrolled to become bookable purohiths. Review their documents and verify them." />

      <div className="mt-7 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat icon={GraduationCap} label="Total enrolled" value={counts.total} />
        <Stat icon={Clock} tint={{ bg: "#FBF3E7", fg: "#B98A2E" }} label="Awaiting review" value={counts.new} />
        <Stat icon={BadgeCheck} tint={{ bg: "#E6F0EE", fg: "#2F7A6B" }} label="Verified" value={counts.verified} />
        <Stat icon={Ban} tint={{ bg: "#FDECEA", fg: "#C62828" }} label="Rejected" value={counts.rejected} />
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-[#E9E2D6] bg-white">
        <div className="hidden grid-cols-12 gap-3 border-b border-[#F0EBE1] bg-[#FAFAF5] px-4 py-3 font-body text-[11px] font-semibold uppercase tracking-wide text-[#A79C8A] md:grid">
          <span className="col-span-3">Applicant</span><span className="col-span-4">Patashala / Matha</span>
          <span className="col-span-2 text-center">Year</span><span className="col-span-2">Submitted</span>
          <span className="col-span-1 text-right">Status</span>
        </div>
        {rows.map((r) => (
          <button key={r.id} onClick={() => setDetail(r)} data-testid={`patashala-row-${r.id}`}
            className="grid w-full grid-cols-1 items-center gap-2 border-b border-[#F5F1E8] px-4 py-3 text-left last:border-0 hover:bg-[#FAF6EF] md:grid-cols-12 md:gap-3">
            <div className="md:col-span-3">
              <p className="font-body text-sm font-semibold text-[#2C241B]">{r.name}</p>
              <p className="font-body text-xs text-[#8A8072]">{r.phone}</p>
            </div>
            <div className="font-body text-sm text-[#5C5449] md:col-span-4">{r.patashala === "Other matha / gurukula" ? r.patashala_other || "Other" : r.patashala}</div>
            <div className="font-body text-sm text-[#5C5449] md:col-span-2 md:text-center">{r.year_completed}</div>
            <div className="font-body text-xs text-[#8A8072] md:col-span-2">{fmtDate(r.created_at)}</div>
            <div className="md:col-span-1 md:text-right"><Badge s={r.status} /></div>
          </button>
        ))}
        {!loading && rows.length === 0 && <div className="px-4 py-16"><Empty>No enrollments yet. Student applications from the Patashala page will appear here.</Empty></div>}
        {loading && <div className="px-4 py-16 text-center font-body text-sm text-[#A79C8A]">Loading…</div>}
      </div>

      {detail && (
        <Modal title={detail.name} onClose={() => setDetail(null)} testid="patashala-modal"
          footer={<>
            <BtnGhost onClick={() => setDetail(null)}>Close</BtnGhost>
            {detail.status !== "rejected" && (
              <button onClick={() => setStatus(detail.id, "rejected")} data-testid="patashala-reject"
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#FDECEA] px-4 py-2 font-body text-sm font-semibold text-[#C62828]">
                <Ban size={15} /> Reject
              </button>
            )}
            {detail.status !== "verified" && (
              <button onClick={() => setStatus(detail.id, "verified")} data-testid="patashala-verify"
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#E6F0EE] px-4 py-2 font-body text-sm font-semibold text-[#2F7A6B]">
                <BadgeCheck size={15} /> Verify
              </button>
            )}
          </>}>
          <div className="flex items-center gap-2"><Badge s={detail.status} /><span className="font-body text-xs text-[#A79C8A]">Submitted {fmtDate(detail.created_at)}</span></div>
          <div className="mt-4 grid grid-cols-2 gap-3 font-body text-sm">
            <div><p className="text-xs text-[#A79C8A]">Phone</p><p className="text-[#2C241B]">{detail.phone}</p></div>
            <div><p className="text-xs text-[#A79C8A]">Email</p><p className="text-[#2C241B]">{detail.email || "—"}</p></div>
            <div className="col-span-2"><p className="text-xs text-[#A79C8A]">Patashala / Matha</p><p className="text-[#2C241B]">{detail.patashala === "Other matha / gurukula" ? detail.patashala_other || "Other" : detail.patashala}</p></div>
            <div><p className="text-xs text-[#A79C8A]">Veda</p><p className="text-[#2C241B]">{detail.veda || "—"}</p></div>
            <div><p className="text-xs text-[#A79C8A]">Shakha</p><p className="text-[#2C241B]">{detail.shakha || "—"}</p></div>
            <div><p className="text-xs text-[#A79C8A]">Year completed</p><p className="text-[#2C241B]">{detail.year_completed}</p></div>
            <div><p className="text-xs text-[#A79C8A]">Area</p><p className="text-[#2C241B]">{detail.area || "—"}</p></div>
            {detail.note && <div className="col-span-2"><p className="text-xs text-[#A79C8A]">Note</p><p className="text-[#2C241B]">{detail.note}</p></div>}
          </div>

          <h4 className="mt-5 font-heading text-base font-semibold text-[#2C241B]">Documents</h4>
          <div className="mt-2 space-y-2">
            {(detail.documents || []).map((d) => (
              <div key={d.key} className="flex items-center justify-between rounded-lg border border-[#F0EBE1] px-3 py-2.5">
                <div className="flex min-w-0 items-center gap-2.5">
                  <FileText size={18} className="shrink-0 text-[#A8722A]" />
                  <div className="min-w-0">
                    <p className="font-body text-sm font-medium text-[#2C241B]">{d.label}</p>
                    <p className="truncate font-body text-xs text-[#A79C8A]">{d.filename || (d.file_id ? "Uploaded" : "Missing")}</p>
                  </div>
                </div>
                <button onClick={() => viewDoc(d.file_id)} disabled={!d.file_id || opening === d.file_id}
                  data-testid={`patashala-doc-${d.key}`}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#E5DFD3] px-3 py-1.5 font-body text-xs font-semibold text-[#5C5449] hover:bg-[#F5F1E8] disabled:opacity-50">
                  {opening === d.file_id ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />} View
                </button>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}
